import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Search, Filter, Download, Eye, Package, 
  Clock, CheckCircle, XCircle, AlertCircle, Truck,
  DollarSign, ShoppingCart 
} from 'lucide-react';

interface Order {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  dojo_name?: string;
  status: 'pending' | 'confirmed' | 'production' | 'shipped' | 'delivered' | 'cancelled';
  total_amount: number;
  items: OrderItem[];
  custom_text: string;
  logo_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  estimated_delivery?: string;
  tracking_number?: string;
}

interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_type: 'bag_tag' | 'keyring';
  quantity: number;
  unit_price: number;
  customizations: any;
}

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Mock data - will be replaced with API calls
  const mockOrders: Order[] = [
    {
      id: 'ORD-001',
      customer_id: 'cust-001',
      customer_name: 'John Smith',
      customer_email: 'john.smith@email.com',
      dojo_name: 'Sydney Karate Club',
      status: 'pending',
      total_amount: 42.50,
      custom_text: 'JOHN SMITH\nSYDNEY KARATE CLUB\nBLACK BELT',
      items: [
        {
          id: 'item-001',
          product_id: 'prod-001',
          product_name: 'Standard Bag Tag',
          product_type: 'bag_tag',
          quantity: 2,
          unit_price: 8.50,
          customizations: {}
        },
        {
          id: 'item-002',
          product_id: 'prod-003',
          product_name: 'Standard Keyring',
          product_type: 'keyring',
          quantity: 5,
          unit_price: 5.50,
          customizations: {}
        }
      ],
      created_at: '2024-10-14T09:30:00Z',
      updated_at: '2024-10-14T09:30:00Z'
    },
    {
      id: 'ORD-002',
      customer_id: 'cust-002',
      customer_name: 'Sarah Johnson',
      customer_email: 'sarah.j@email.com',
      dojo_name: 'Melbourne Martial Arts',
      status: 'production',
      total_amount: 75.00,
      custom_text: 'SARAH JOHNSON\nMELBOURNE MARTIAL ARTS\n2ND DAN',
      items: [
        {
          id: 'item-003',
          product_id: 'prod-002',
          product_name: 'Premium Bag Tag',
          product_type: 'bag_tag',
          quantity: 5,
          unit_price: 15.00,
          customizations: {}
        }
      ],
      created_at: '2024-10-13T14:20:00Z',
      updated_at: '2024-10-14T10:15:00Z',
      estimated_delivery: '2024-10-20T00:00:00Z'
    },
    {
      id: 'ORD-003',
      customer_id: 'cust-003',
      customer_name: 'Mike Chen',
      customer_email: 'mike.chen@email.com',
      dojo_name: 'Brisbane Karate Dojo',
      status: 'shipped',
      total_amount: 102.00,
      custom_text: 'MIKE CHEN\nBRISBANE KARATE DOJO\nSENSEI',
      tracking_number: 'AUS1234567890',
      items: [
        {
          id: 'item-004',
          product_id: 'prod-002',
          product_name: 'Premium Bag Tag',
          product_type: 'bag_tag',
          quantity: 3,
          unit_price: 15.00,
          customizations: {}
        },
        {
          id: 'item-005',
          product_id: 'prod-004',
          product_name: 'Premium Keyring',
          product_type: 'keyring',
          quantity: 9,
          unit_price: 12.00,
          customizations: {}
        }
      ],
      created_at: '2024-10-12T11:45:00Z',
      updated_at: '2024-10-14T08:30:00Z',
      estimated_delivery: '2024-10-16T00:00:00Z'
    },
    {
      id: 'ORD-004',
      customer_id: 'cust-004',
      customer_name: 'Emma Davis',
      customer_email: 'emma.davis@email.com',
      status: 'delivered',
      total_amount: 34.00,
      custom_text: 'EMMA DAVIS\nADELAIDE KARATE CLUB\nBROWN BELT',
      tracking_number: 'AUS9876543210',
      items: [
        {
          id: 'item-006',
          product_id: 'prod-001',
          product_name: 'Standard Bag Tag',
          product_type: 'bag_tag',
          quantity: 4,
          unit_price: 8.50,
          customizations: {}
        }
      ],
      created_at: '2024-10-10T16:00:00Z',
      updated_at: '2024-10-13T12:00:00Z'
    }
  ];

  useEffect(() => {
    // TODO: Fix type mismatch between API and component Order types
    // For now, use mock data while we test the order submission flow
    setTimeout(() => {
      setOrders(mockOrders);
      setLoading(false);
    }, 500);
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.dojo_name && order.dojo_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const updateOrderStatus = (orderId: string, newStatus: Order['status']) => {
    setOrders(orders.map(order => 
      order.id === orderId 
        ? { 
            ...order, 
            status: newStatus, 
            updated_at: new Date().toISOString(),
            ...(newStatus === 'shipped' && !order.tracking_number ? { tracking_number: `AUS${Date.now()}` } : {})
          }
        : order
    ));
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'confirmed': return <CheckCircle className="h-4 w-4" />;
      case 'production': return <Package className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'delivered': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'production': return 'bg-purple-100 text-purple-800';
      case 'shipped': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    production: orders.filter(o => o.status === 'production').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    revenue: orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + o.total_amount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="mt-2 text-gray-600">Manage customer orders and production queue</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Production</p>
                <p className="text-2xl font-bold text-purple-600">{stats.production}</p>
              </div>
              <Package className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Shipped</p>
                <p className="text-2xl font-bold text-orange-600">{stats.shipped}</p>
              </div>
              <Truck className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revenue</p>
                <p className="text-2xl font-bold text-green-600">${stats.revenue.toFixed(2)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search orders by ID, customer name, email, or dojo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="production">In Production</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Order Queue</CardTitle>
          <CardDescription>
            {filteredOrders.length} of {orders.length} orders shown
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No orders found</p>
              {searchTerm && (
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Order</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Customer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Items</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Total</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{order.id}</p>
                          {order.tracking_number && (
                            <p className="text-sm text-gray-500">Track: {order.tracking_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{order.customer_name}</p>
                          <p className="text-sm text-gray-500">{order.customer_email}</p>
                          {order.dojo_name && (
                            <p className="text-sm text-gray-500">{order.dojo_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {order.items.map((item, idx) => (
                            <p key={idx} className="text-gray-600">
                              {item.quantity}x {item.product_name}
                            </p>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">${order.total_amount.toFixed(2)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            <span className="ml-1 capitalize">{order.status}</span>
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDetails(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value as Order['status'])}
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="production">Production</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-screen overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Order Details - {selectedOrder.id}</CardTitle>
                  <CardDescription>
                    Created on {new Date(selectedOrder.created_at).toLocaleDateString()}
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={() => setShowOrderDetails(false)}>
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Name</p>
                    <p className="text-gray-900">{selectedOrder.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Email</p>
                    <p className="text-gray-900">{selectedOrder.customer_email}</p>
                  </div>
                  {selectedOrder.dojo_name && (
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-gray-600">Dojo</p>
                      <p className="text-gray-900">{selectedOrder.dojo_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Order Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Product</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Qty</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Price</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item) => (
                        <tr key={item.id} className="border-t">
                          <td className="py-2 px-3">
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              <p className="text-sm text-gray-500 capitalize">{item.product_type.replace('_', ' ')}</p>
                            </div>
                          </td>
                          <td className="py-2 px-3">{item.quantity}</td>
                          <td className="py-2 px-3">${item.unit_price.toFixed(2)}</td>
                          <td className="py-2 px-3 font-medium">${(item.quantity * item.unit_price).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-gray-50">
                        <td colSpan={3} className="py-2 px-3 font-medium text-right">Total:</td>
                        <td className="py-2 px-3 font-bold">${selectedOrder.total_amount.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Custom Text */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Custom Text</h3>
                <div className="bg-gray-50 p-3 rounded border">
                  <pre className="whitespace-pre-wrap text-sm font-mono">{selectedOrder.custom_text}</pre>
                </div>
              </div>

              {/* Status and Tracking */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Status & Tracking</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Current Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`}>
                      {getStatusIcon(selectedOrder.status)}
                      <span className="ml-1 capitalize">{selectedOrder.status}</span>
                    </span>
                  </div>
                  {selectedOrder.tracking_number && (
                    <div>
                      <p className="text-sm font-medium text-gray-600">Tracking Number</p>
                      <p className="text-gray-900 font-mono">{selectedOrder.tracking_number}</p>
                    </div>
                  )}
                  {selectedOrder.estimated_delivery && (
                    <div>
                      <p className="text-sm font-medium text-gray-600">Estimated Delivery</p>
                      <p className="text-gray-900">{new Date(selectedOrder.estimated_delivery).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline">Print Order</Button>
                <Button variant="outline">Send Update</Button>
                <Button>Edit Order</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}